import React, { useState } from 'react';
import { CreditCard, Banknote, CheckCircle2, ChevronDown, ExternalLink, ShieldCheck, Info } from 'lucide-react';

const EZCARE_URL = 'https://ezcare.easyms.co.kr/?PG=bank';
const STAGE = (s) => (s === 'not_applied' ? ['신청 전', 'bg-gray-100 text-gray-600']
  : s === 'active' ? ['이용 중', 'bg-emerald-100 text-emerald-700']
    : s === 'suspended' ? ['이용 중지', 'bg-rose-100 text-rose-700']
      : ['approved', 'connection_required', 'rejected'].includes(s) ? ['승인·연결 확인 필요', 'bg-sky-100 text-sky-700']
        : ['신청 중', 'bg-amber-100 text-amber-700']);

const Section = ({ title, children }) => (
  <div className="space-y-1.5"><p className="text-xs font-black">{title}</p><div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">{children}</div></div>
);
const Bullets = ({ items }) => <ul className="space-y-1">{items.map((t) => <li key={t} className="flex gap-1.5"><CheckCircle2 size={13} className="mt-0.5 shrink-0 text-emerald-600" />{t}</li>)}</ul>;
const Note = ({ children, testid }) => <p data-testid={testid} className="flex gap-1.5 rounded-xl bg-amber-50 text-amber-800 p-2 text-[11px]"><Info size={13} className="mt-0.5 shrink-0" />{children}</p>;

const FeeTable = () => (
  <table className="w-full text-xs border-collapse" data-testid="card-fee-table">
    <thead><tr className="bg-black/[0.04]"><th className="text-left p-2">사업자 구분</th><th className="text-right p-2">카드결제 수수료</th></tr></thead>
    <tbody>
      <tr className="bg-emerald-50"><td className="p-2 font-bold">영세·중소 사업자 (개인/법인) <span className="ml-1 px-1.5 rounded-full bg-emerald-600 text-white text-[10px]">우대</span></td><td className="p-2 text-right font-black text-emerald-700">1.9% ~ 2.85%</td></tr>
      <tr><td className="p-2 font-bold">일반 사업자 (개인/법인)</td><td className="p-2 text-right font-black">3.4%</td></tr>
    </tbody>
  </table>
);

const CardDetail = () => (
  <div className="space-y-4" data-testid="guide-card-detail">
    <Section title="주요 서비스"><p>소비자가 신용카드로 결제하면 결제 결과가 LIVEMOA 주문에 반영됩니다. 카드 승인·취소·부분취소와 환불내역을 관리할 수 있습니다.</p></Section>
    <Section title="카드결제 수수료"><FeeTable /><p data-testid="card-fee-note">카드결제 수수료는 사업자 유형과 가맹점 심사 결과에 따라 적용됩니다. 실제 적용 수수료 및 부가세는 라이트페이 계약조건을 확인해 주세요.</p></Section>
    <Section title="정산 안내">
      <p data-testid="card-settle-note">기본 정산주기는 D+5영업일이며, 실제 정산일은 가맹점 계약조건에 따릅니다.</p>
      <p className="text-[11px] text-gray-500">D+3·D+1영업일 정산은 추가 수수료 및 심사조건이 적용될 수 있습니다.</p>
    </Section>
    <Section title="가입 준비사항">
      <Bullets items={['사업자등록정보', '대표자 및 담당자 정보', '판매 상품·업종 정보', '쇼핑몰 또는 판매 페이지 주소', '정산계좌', '공급사가 요청하는 계약서류']} />
      <p className="text-[11px] text-gray-500">추가 필요서류는 개인사업자·법인사업자에 따라 다를 수 있습니다.</p>
    </Section>
    <Section title="신용카드 전용 안내">
      <Note testid="card-only-note">LIVEMOA는 라이트페이 신용카드 결제만 제공합니다. 가입 페이지에는 다른 결제수단이 표시될 수 있으며, 신용카드 전용 MID 적용 여부는 관리자 확인 후 활성화됩니다.</Note>
    </Section>
    <Section title="가입 절차"><p>카드결제 신청 → 라이트페이 가입·심사 → 관리자 확인 → MID 연결 → 이용 시작</p></Section>
  </div>
);

const BankDetail = ({ go }) => (
  <div className="space-y-4" data-testid="guide-bank-detail">
    <Section title="주요 서비스"><p>고객이 판매자의 기존 은행계좌에 입금하면 바로빌이 입출금 거래내역을 수집하고, LIVEMOA가 주문과 대조합니다. 입금자명·금액·계좌·시간 조건이 모두 일치하면 자동으로 결제완료 처리하고, 일치하지 않는 거래는 확인 필요 상태로 보류합니다.</p></Section>
    <Section title="서비스 기능">
      <Bullets items={['기존 은행계좌 이용', '개인·법인 계좌 등록', '여러 계좌 관리', '10분 주기 거래 수집', '입금·출금 내역 확인', '입금내역 주문 자동매칭', 'OMS 결제완료 반영', '중복 거래 처리 방지']} />
      <p className="text-[11px] text-gray-500" data-testid="bank-interval-note">10분은 거래 수집 주기이며, 입금 후 10분 이내 결제완료를 보장하지 않습니다.</p>
    </Section>
    <Section title="이용료 안내">
      <p data-testid="bank-fee-plan"><b>계좌당 월 10,000원 (부가세 별도)</b> <span className="ml-1 px-1.5 whitespace-nowrap rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">예정 이용료</span></p>
      <p className="text-[11px] text-gray-500">운영 과금 설정 및 계약 확정 전 예정 금액입니다. 이용료는 바로빌에서 직접 결제하며, LIVEMOA 충전금이나 카드에서 결제되지 않습니다.</p>
    </Section>
    <Section title="가입 전 준비사항">
      <Note testid="bank-quick-lookup-note">이용할 은행의 빠른조회(간편조회) 서비스를 먼저 등록해 주세요.</Note>
      <p>빠른조회는 인증서 없이 거래내역을 조회할 수 있게 하는 은행 서비스로, 바로빌이 입금내역을 자동 수집하려면 필요합니다. 거래 은행 공식 인터넷뱅킹에서 '빠른조회' 또는 '간편조회' 메뉴로 등록합니다.</p>
      <a href={EZCARE_URL} target="_blank" rel="noopener noreferrer" data-testid="bank-quick-lookup-link" className="inline-flex items-center gap-1 font-bold underline">은행별 빠른조회 방법 보기 <ExternalLink size={12} /></a>
      <p className="text-[11px] text-gray-500">위 링크는 은행 공식 사이트가 아닌 외부 참고자료입니다. 실제 등록은 해당 은행 공식 사이트에서 진행해 주세요.</p>
    </Section>
    <Section title="가입 절차">
      <ol className="list-decimal pl-4 space-y-0.5">{['판매자 사업자정보 확인', '바로빌 계정 설정', '바로빌 회원사 등록 (기존 회원은 회원 연결)', '은행 빠른조회 등록 확인', '바로빌 공식 화면에서 계좌 등록·인증', '바로빌 이용료 결제', '계좌·이용상태 확인 후 자동입금확인 활성화'].map((t) => <li key={t}>{t}</li>)}</ol>
    </Section>
    <Section title="계좌 인증 보안">
      <p className="flex gap-1.5"><ShieldCheck size={13} className="mt-0.5 shrink-0 text-emerald-600" />계좌 비밀번호와 은행 빠른조회 인증정보는 바로빌 공식 화면에서 입력합니다. LIVEMOA는 은행 인증정보를 수집하지 않습니다.</p>
    </Section>
    <div className="flex flex-wrap gap-1.5">
      {[['신규 바로빌 가입', 'bank-go-new'], ['이미 바로빌 회원', 'bank-go-existing'], ['계좌 등록·관리', 'bank-go-accounts'], ['이용료 결제', 'bank-go-fee'], ['신청 현황 확인', 'bank-go-status']].map(([l, t]) => (
        <button key={t} type="button" onClick={go} data-testid={t} className="px-3 py-1.5 rounded-full border text-xs font-bold hover:bg-black/5">{l}</button>
      ))}
    </div>
  </div>
);

const GuideCard = ({ icon: Icon, title, sub, points, summary, status, applyLabel, onApply, testid, children }) => {
  const [open, setOpen] = useState(false);
  const [label, cls] = STAGE(status);
  const cta = status === 'not_applied' ? applyLabel : status === 'active' ? '이용 관리' : '신청 현황 보기';
  return (
    <div className="rounded-3xl border bg-white dark:bg-white/5 p-4 sm:p-5 space-y-3" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex gap-3"><span className="h-10 w-10 shrink-0 rounded-2xl bg-black text-white grid place-items-center"><Icon size={18} /></span>
          <div><p className="text-lg font-black leading-tight">{title}</p><p className="text-xs text-gray-500">{sub}</p></div></div>
        <span data-testid={`${testid}-stage`} className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-black ${cls}`}>{label}</span>
      </div>
      <Bullets items={points} />
      {summary}
      {open && <div className="pt-3 border-t">{children}</div>}
      <div className="flex flex-col sm:flex-row gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`${testid}-detail-toggle`} className="flex-1 inline-flex items-center justify-center gap-1 py-2.5 rounded-full border text-sm font-bold">
          {open ? '접기' : '서비스 자세히 보기'}<ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} /></button>
        <button type="button" onClick={onApply} data-testid={`${testid}-cta`} className="flex-1 py-2.5 rounded-full bg-black text-white text-sm font-black">{cta}</button>
      </div>
    </div>
  );
};

const CARD_ST = { not_applied: 'not_applied', received: 'submitted', reviewing: 'reviewing', docs: 'additional_info_required', contract_check: 'approved', mid_required: 'connection_required', pg_verify_required: 'connection_required', activation_wait: 'connection_required', active: 'active', suspended: 'suspended' };

export const PaymentServiceGuide = ({ d, setTab }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start" data-testid="payment-service-guide">
    <GuideCard icon={CreditCard} title="카드결제" sub="고객의 카드결제를 간편하게 받으세요." testid="guide-card" status={CARD_ST[d.card.stage?.key] || d.card.onboarding.status}
      applyLabel="카드결제 신청하기" onApply={() => setTab('card')}
      points={['신용카드 결제 · 결제완료 주문 자동반영', '전액취소·부분취소 · 결제·환불 내역 관리', '판매자별 가맹점 계약', '카드대금 PG 정산']}
      summary={<p className="rounded-xl bg-emerald-50 p-2 text-xs" data-testid="card-fee-summary">수수료 <b className="text-emerald-700">영세·중소 1.9% ~ 2.85%</b> · 일반 3.4% · 기본 D+5영업일 · D+3영업일 선택 가능</p>}>
      <CardDetail />
    </GuideCard>
    <GuideCard icon={Banknote} title="무통장입금 자동확인" sub="입금 확인부터 주문 처리까지 자동으로 관리하세요." testid="guide-bank" status={d.bank.onboarding.status}
      applyLabel="자동입금확인 신청하기" onApply={() => setTab('bank')}
      points={['기존 은행계좌 사용 · 10분 주기 거래조회', '입금자명·금액 자동대조', 'OMS 결제완료 자동반영 · 입출금내역 관리', '계좌별 월 이용료']}
      summary={<p className="rounded-xl bg-black/[0.03] p-2 text-xs" data-testid="bank-fee-summary">계좌당 월 10,000원 (부가세 별도) · <span className="font-bold text-amber-700">예정 이용료</span></p>}>
      <BankDetail go={() => setTab('bank')} />
    </GuideCard>
  </div>
);
